import { NgIf } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { LocationSilo } from '@next/core-lib/cart';
import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, AgrDialogModule } from '@next/core-lib/dialog';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { SelectedCustomer, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { addSiloRequest, updateSiloRequest } from 'src/app/store/actions';

interface EditSiloDialogData extends AbstractDialogData {
  silo?: LocationSilo;
}
@Component({
  selector: 'app-edit-silo',
  templateUrl: './edit-silo.component.html',
  styleUrls: ['./edit-silo.component.scss'],
  imports: [AgrDialogModule, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, NgIf, MatError, AgrI18nSharedModule],
})
export class EditSiloDialogComponent extends AbstractDialog<EditSiloDialogData> implements OnInit, OnDestroy {
  @ViewChild('siloNameInput') siloNameInput: ElementRef;
  @ViewChild('siloNumberInput') siloNumberInput: ElementRef;

  formGroup = new FormGroup({
    siloName: new FormControl(this.data.silo ? this.data.silo.name : ''),
    siloNumber: new FormControl(this.data.silo ? this.data.silo.siloNumber : '', [Validators.required, Validators.max(9999)]),
  });

  readonly destroy$ = new Subject<void>();

  customer: SelectedCustomer;
  customer$ = this.store
    .select(UserSelectors.selectCustomer)
    .pipe(
      takeUntil(this.destroy$),
      tap((customer: SelectedCustomer) => {
        this.customer = customer;
      }),
    )
    .subscribe();

  location: SelectedLocation;
  shipTo$ = this.store
    .select(UserSelectors.selectLocation)
    .pipe(
      takeUntil(this.destroy$),
      tap((location) => {
        this.location = location;
      }),
    )
    .subscribe();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EditSiloDialogData,
    protected readonly mdDialogRef: MatDialogRef<EditSiloDialogComponent, AbstractDialogResultData<EditSiloDialogData>>,
    private readonly store: Store,
    private readonly localeService: LocaleService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.data.title = this.data.silo ? this.localeService.translate('silos.edit-silo') : this.localeService.translate('silos.add-silo');
    this.data.subTitle = this.data.silo
      ? `${this.localeService.translate('silos.edit-silo-subtitle')} ${this.location.name}`
      : `${this.localeService.translate('silos.add-silo-subtitle')}  ${this.location.name}`;
    this.data.confirmButton = true;
    this.data.confirmButtonText = this.data.silo ? this.localeService.translate('silos.edit-silo') : this.localeService.translate('silos.add-silo');
    this.data.disableConfirmButton = !this.formGroup.valid;
    this.formGroup.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.data.disableConfirmButton = !this.formGroup.valid;
        }),
      )
      .subscribe();

    if (this.data.silo) {
      this.formGroup.controls['siloNumber'].disable();
    }
  }

  createOrEditSilo() {
    if (this.data.silo) {
      this.store.dispatch(
        updateSiloRequest({
          silo: {
            ...this.data.silo,
            name: this.siloNameInput.nativeElement.value,
          },
        }),
      );
    } else {
      this.store.dispatch(
        addSiloRequest({
          silo: {
            id: 0,
            name: this.siloNameInput.nativeElement.value,
            siloNumber: this.siloNumberInput.nativeElement.value,
            customerId: this.customer.id,
            shipToId: this.location.id,
          },
        }),
      );
    }

    this.confirm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
